import { ChevronRight } from 'lucide-react';
import type { WebGoal } from '../../../lib/goals';
import { goalMembers, membersDone } from '../../../lib/goals';
import { goalArc, goalBadge, knownGoalOutcome } from '../../../lib/goal-status';
import { useTranslation } from '../../../lib/i18n';
import { useLocale } from '../../../lib/locale-context';
import { formatGoalEndDate, goalEndLine } from '../../../lib/goal-list-sections';
import { Badge, Card, ProgressRing, Typography, useTruncatedTitle, type BadgeVariant } from '../ui';
import { useCategoryRowClass, useCategoryTitleClass } from '../category-view/CategoryView';
import styles from './GoalCard.module.css';

// One goal card for the Goals-tab grid (Figma 10666:89977 / 10666:90268): an 80px completion ring beside
// the status badge over the goal name, and a trailing chevron (as in the Figma and native's
// `ChevronRightIcon`). Clicking anywhere on it opens the goal's plan in the shared side panel. Mirrors the
// native `GoalRow` — same 80/12 ring, same badge-over-title stack, and the same rule that a 100%-complete
// plan reads "Done" whatever the stored entity status says.
//
// A goal made of MEMBERS reads its members here too: the ring already counts them (the route rolls members
// into `percent` alongside the goal's own tasks), and a "{done} of {total} members done" line under the
// title says what the ring is counting — otherwise a container goal's progress would have no visible
// source on the card. The badge defers to a recorded OUTCOME when there is one, so a goal that was called
// off never wears the green Done chip.
export function GoalCard({
  goal,
  onOpen,
  groupBadge,
}: {
  goal: WebGoal;
  onOpen: () => void;
  /** In a grouped list the row wears its GROUP's badge, the one the status menu names (Figma 21055:254338). */
  groupBadge?: { labelKey: string; variant: BadgeVariant };
}) {
  const { t } = useTranslation();
  // A recorded outcome ("Partly reached", "Called off") outranks the group's generic name, as it does the
  // status: a goal called off must not read as "Done".
  const badge = groupBadge && !knownGoalOutcome(goal.outcome) ? groupBadge : goalBadge(goal);
  const members = goalMembers(goal);
  const { locale } = useLocale();
  const endLine = goalEndLine(goal);
  const rowSurface = useCategoryRowClass();
  const titleCap = useCategoryTitleClass();
  const brief = goal.brief?.trim();
  // The row is a button, so the cut-off description's tooltip sits on the row itself (see `useTruncatedTitle`).
  const briefTip = useTruncatedTitle<HTMLSpanElement>(brief);
  return (
    <Card component="button" variant="shadow" interactive className={rowSurface ? `${styles.card} ${rowSurface}` : styles.card}
      onClick={onOpen}
      title={briefTip.title}
      onMouseEnter={briefTip.measure}
      onFocus={briefTip.measure}
    >
      <ProgressRing percent={goal.percent} arcColor={goalArc(goal)} size={64} stroke={10} />
      <span className={styles.body}>
        <span className={styles.badgeInline}>
          <Badge variant={badge.variant} label={t(badge.labelKey)} />
        </span>
        <span className={titleCap ? `${styles.title} ${titleCap}` : styles.title}>{goal.title || t('goals.untitled')}</span>
        {/* One line, ellipsised; the whole description shows on hover when it does not fit. */}
        {brief && (
          <span ref={briefTip.ref} className={`sym-type-heading-04 ${styles.brief}`}>
            {brief}
          </span>
        )}
        {endLine && (
          <Typography variant="p3" color="neutral" component="span" className={styles.members}>
            {t(endLine.key, { date: formatGoalEndDate(endLine.endsAt, locale) })}
          </Typography>
        )}
        {members.length > 0 && (
          <Typography variant="p3" color="neutral" component="span" className={styles.members}>
            {t('goals.members.count', { done: membersDone(members), total: members.length })}
          </Typography>
        )}
      </span>
      {/* A list row (Figma 21055:254336) ends with the status badge; a narrow pane keeps it over the title
          and the chevron at the end, as the phones draw the card. */}
      <span className={styles.badgeEnd}>
        <Badge variant={badge.variant} label={t(badge.labelKey)} />
      </span>
      <ChevronRight aria-hidden size={20} className={`${styles.chevron} dir-flip`} />
    </Card>
  );
}
